import {chatReducer, type ChatTurnCancelledAction, type ToolCallResult} from '@microsoft/agent-host-protocol'
import type {ConnectionAdmission} from '@github-ui/ahp-relay/connection-admission'
import type {SessionApprovalMode} from '@github-ui/ahp-relay/permission-mode'
import {observeRelayRecoveryPreparation, type RelayLease} from '@github-ui/ahp-relay/relay-registry'
import type {RelaySessionRenameOptions} from '@github-ui/ahp-relay/relay-connection-runtime'
import {
  clientCommandObserver,
  bindClientCommand,
  clientStopObserver,
  observeMutationWait,
  observeSessionCommand,
  observeQueuedCommandRevision,
  type ClientCommandObserver,
} from './client-interaction-observer'
import {
  EstablishedAhpSessionResumptionTimeoutError,
  type DispatchReceipt,
  type DirectTurnTelemetryContext,
  type EstablishedAhpDirectTurnTelemetry,
  type EstablishedAhpResumptionAttempt,
  type EstablishedAhpSessionError,
  type EstablishedAhpPendingMessage,
  type EstablishedAhpSessionCommands,
  type EstablishedAhpSessionIdentity,
  type EstablishedAhpSessionInputController,
  type EstablishedAhpSessionView,
  type EstablishedAhpSessionViewSnapshot,
  type InputCompletion,
  type PreparedPrompt,
  type StopOutcome,
  type SubmissionAttempt,
  type SubmissionRecovery,
  type ToolConfirmationResponse,
} from './contracts'
import {
  SubmissionAttemptStore,
  SubmissionRecoveryError,
  submissionIsUnsettled,
  submissionStoreFor,
} from './submission-recovery'
import {UserFacingSubmissionError} from './submission-error'
import {createEstablishedAhpResumptionDeadline} from './resumption-deadline'

function attachmentsFor(prompt: PreparedPrompt) {
  return prompt.attachments && prompt.attachments.length > 0 ? [...prompt.attachments] : undefined
}

const ACTION_TURN_CANCELLED = 'chat/turnCancelled' as ChatTurnCancelledAction['type']

/**
 * React-free command and view client for one identity-bound relay lease.
 *
 * The client intentionally does not own the lease lifecycle. Its containing
 * established-session runtime owns acquisition, reset, and disposal.
 */
export class AhpSessionClient {
  readonly view: EstablishedAhpSessionView
  readonly input: EstablishedAhpSessionInputController
  readonly commands: EstablishedAhpSessionCommands
  readonly identity: EstablishedAhpSessionIdentity
  private readonly lease: RelayLease
  private readonly directTurnTelemetry: EstablishedAhpDirectTurnTelemetry | undefined
  readonly #onTerminalResumptionTimeout: (error: EstablishedAhpSessionError) => void
  readonly #isResumptionRecoveryActive: () => boolean
  readonly #resumptionAttemptForMutation: (start: boolean) => EstablishedAhpResumptionAttempt | undefined

  #clientSeq = 0
  #disposed = false
  #subscriptionsReleased = false
  #terminalError: EstablishedAhpSessionError | undefined
  #viewSnapshot!: EstablishedAhpSessionViewSnapshot
  #optimisticallyCancelledTurnId: string | undefined
  #cancellationVersion = 0
  #invalidatePreparation: (() => void) | undefined
  #liveness: Promise<void> | undefined
  readonly #viewListeners = new Set<() => void>()
  readonly #disposeListeners = new Set<(error: Error) => void>()
  readonly #unsubscribeView: () => void
  readonly #unsubscribeActions: () => void
  readonly #unsubscribeDispatches: () => void
  readonly #submissionStore: SubmissionAttemptStore
  readonly #recovery: SubmissionRecovery

  constructor(
    identity: EstablishedAhpSessionIdentity,
    lease: RelayLease,
    directTurnTelemetry?: EstablishedAhpDirectTurnTelemetry,
    onTerminalResumptionTimeout: (error: EstablishedAhpSessionError) => void = () => {},
    isResumptionRecoveryActive: () => boolean = () => true,
    resumptionAttemptForMutation: (start: boolean) => EstablishedAhpResumptionAttempt | undefined = () => undefined,
  ) {
    this.identity = identity
    this.lease = lease
    this.directTurnTelemetry = directTurnTelemetry
    this.#onTerminalResumptionTimeout = onTerminalResumptionTimeout
    this.#isResumptionRecoveryActive = isResumptionRecoveryActive
    this.#resumptionAttemptForMutation = resumptionAttemptForMutation
    this.#submissionStore = lease.dispatchTurnTracked ? submissionStoreFor(identity) : new SubmissionAttemptStore()
    this.#recovery = {
      getSnapshot: this.#submissionStore.getSnapshot,
      subscribe: this.#submissionStore.subscribe,
      waitForAccepted: (receipt, timeoutMs) => this.waitForAccepted(receipt, timeoutMs),
      preparationFailed: lease.dispatchTurnTracked
        ? (prompt, cause, requestId = crypto.randomUUID()) => {
            this.ensureActive()
            const blocker = this.#submissionBlocker(cause)
            if (blocker) return blocker
            this.#submissionStore.begin(requestId, prompt)
            this.#submissionStore.failed(requestId)
            return new SubmissionRecoveryError(this.#recovery, 'unsent', cause)
          }
        : undefined,
      recover: async () => {
        this.ensureActive()
        const originalAttempt = this.#submissionStore.getSnapshot()
        const isCurrentAttempt = () => {
          const current = this.#submissionStore.getSnapshot()
          // begin clones the prompt; phase updates preserve that attempt's snapshot.
          return (
            current?.requestId === originalAttempt?.requestId &&
            current?.prompt === originalAttempt?.prompt &&
            (!originalAttempt?.dispatch || current?.dispatch?.turnId === originalAttempt.dispatch.turnId)
          )
        }
        if (!this.lease.refresh) throw new SubmissionRecoveryError(this.#recovery, 'unknown')
        try {
          const deadline = createEstablishedAhpResumptionDeadline()
          try {
            await deadline.waitFor(this.lease.refresh())
          } finally {
            deadline.dispose()
          }
          this.ensureActive()
          if (!isCurrentAttempt()) return undefined
          this.#submissionStore.reconcile(this.lease.getSnapshot())
          const attempt = this.#submissionStore.getSnapshot()
          if (attempt?.receipt && submissionIsUnsettled(attempt)) await this.waitForAccepted(attempt.receipt)
          return isCurrentAttempt() ? this.#submissionStore.getSnapshot() : undefined
        } catch (error) {
          if (!isCurrentAttempt()) return undefined
          throw error
        }
      },
    }
    this.#submissionStore.reconcile(this.lease.getSnapshot())
    this.#viewSnapshot = this.readViewSnapshot()
    this.directTurnTelemetry?.observedState(this.lease.getSnapshot())
    this.#updateTelemetryReadiness()
    this.#unsubscribeView = this.lease.subscribe(() => {
      if (this.#disposed || !this.lease.isCurrent()) return
      this.#viewSnapshot = this.readViewSnapshot()
      this.directTurnTelemetry?.observedState(this.lease.getSnapshot())
      this.#submissionStore.reconcile(this.lease.getSnapshot())
      this.#updateTelemetryReadiness()
      for (const listener of this.#viewListeners) listener()
    })
    this.#unsubscribeActions = directTurnTelemetry
      ? this.lease.subscribeActions((envelope, previousState, state) =>
          directTurnTelemetry.acceptedAction(envelope, previousState, state),
        )
      : () => {}
    this.#unsubscribeDispatches =
      this.lease.subscribeTurnDispatches?.(envelope => {
        this.#submissionStore.action(envelope)
      }) ?? (() => {})
    this.view = {
      generation: lease.generation,
      isCurrent: () => lease.isGenerationCurrent(),
      getSnapshot: () => this.#viewSnapshot,
      subscribe: listener => {
        this.#viewListeners.add(listener)
        return () => this.#viewListeners.delete(listener)
      },
      observeChat: (channel, observer) => this.lease.observeChat(channel, observer),
      observeChangeset: (channel, observer) => this.lease.observeChangeset(channel, observer),
      observeTerminal: (channel, observer) => this.lease.observeTerminal(channel, observer),
      readResource: uri => this.lease.readResource(uri),
      subscribeTelemetry: observer => this.lease.subscribeTelemetry(observer),
    }

    this.input = {
      recovery: this.#recovery,
      submit: (prompt, requestId, beforeDispatch, directTurnTelemetryContext) =>
        this.submit(prompt, requestId, beforeDispatch, directTurnTelemetryContext),
      queue: (prompt, beforeDispatch) => this.queue(prompt, beforeDispatch),
      steer: (prompt, steeringId, beforeDispatch, directTurnRequestId) =>
        this.steer(prompt, steeringId, beforeDispatch, directTurnRequestId),
      sendNow: message => this.sendNow(message),
      removeQueued: id => this.removeQueued(id),
    }

    this.commands = {
      cancel: turnId => this.cancel(turnId),
      claimActiveClient: tools => this.claimActiveClient(tools),
      completions: (text, offset) => this.lease.completions(text, offset),
      respondToPrompt: response => this.respondToPrompt(response),
      confirmTool: confirmation => this.confirmTool(confirmation),
      completeTool: input => this.completeTool(input),
      renameSession: (title, options?: RelaySessionRenameOptions) => {
        this.ensureActive()
        return this.lease.renameSession(title, options)
      },
      setApprovalMode: mode => this.setApprovalMode(mode),
      setApproveAll: enabled => this.setApproveAll(enabled),
      setMode: mode => this.setMode(mode),
      ensureLive: admission => this.ensureLive(admission),
    }
  }

  private ensureLive(admission?: ConnectionAdmission): Promise<void> {
    if (admission) return this.checkLiveness(admission)
    this.#liveness ??= (async () => {
      try {
        await this.lease.ensureLive()
      } catch {
        // Liveness failures surface through lease status.
      } finally {
        this.#liveness = undefined
      }
    })()
    return this.#liveness
  }

  private async checkLiveness(admission?: ConnectionAdmission): Promise<void> {
    try {
      await this.lease.ensureLive(undefined, admission)
    } catch {
      // Liveness failures surface through lease status.
    }
  }

  private readViewSnapshot(): EstablishedAhpSessionViewSnapshot {
    const state = this.lease.getSnapshot()
    const readiness = this.lease.getReadiness?.()
    const connectionProgress = this.lease.getConnectionProgress?.()
    if (this.#optimisticallyCancelledTurnId && state?.activeTurn?.id !== this.#optimisticallyCancelledTurnId) {
      this.#optimisticallyCancelledTurnId = undefined
    }
    const optimisticState =
      state && this.#optimisticallyCancelledTurnId
        ? chatReducer(state, {
            type: ACTION_TURN_CANCELLED,
            turnId: this.#optimisticallyCancelledTurnId,
            duration: 0,
          })
        : state
    return {
      ...(readiness ? {readiness} : {}),
      ...(connectionProgress ? {connectionProgress} : {}),
      state: optimisticState,
      status: this.lease.getStatus(),
      reachable: this.lease.getReachable(),
      error: this.lease.getError(),
      failure: this.lease.getFailure?.(),
      modelFailures: this.lease.getModelFailures(),
      provisioning: this.lease.getProvisioning(),
      rootState: this.lease.getRootState?.(),
      sessionState: this.lease.getSessionState(),
      passive: this.lease.getPassive(),
      passiveKnown: this.lease.getPassiveKnown(),
      steeringMessages: this.lease.getSteeringMessages(),
    }
  }

  #updateTelemetryReadiness(): void {
    this.directTurnTelemetry?.readinessChanged(
      this.lease.getStatus() === 'connected' &&
        this.lease.getReachable() &&
        this.lease.getPassiveKnown() &&
        !this.lease.getPassive(),
    )
  }

  #releaseSubscriptions(): void {
    if (this.#subscriptionsReleased) return
    this.#subscriptionsReleased = true
    this.#unsubscribeView()
    this.#unsubscribeActions()
    this.#unsubscribeDispatches()
  }

  markDisposed(): void {
    if (this.#disposed) return
    this.#disposed = true
    this.#invalidatePreparation?.()
    this.directTurnTelemetry?.readinessChanged(false)
    const error = new Error('established AHP session client is disposed')
    for (const listener of this.#disposeListeners) listener(error)
    this.#disposeListeners.clear()
    this.#releaseSubscriptions()
    this.#viewListeners.clear()
  }

  markResumptionTimedOut(error: EstablishedAhpSessionError): void {
    if (this.#disposed || this.#terminalError) return
    this.#terminalError = error
    this.#invalidatePreparation?.()
    this.#releaseSubscriptions()
    this.#viewSnapshot = {
      ...this.#viewSnapshot,
      status: 'error',
      reachable: false,
      error: error.message,
      failure: error.failure,
    }
    this.directTurnTelemetry?.readinessChanged(false)
    for (const listener of this.#viewListeners) listener()
  }

  async waitForAccepted(receipt: DispatchReceipt, timeoutMs = 30_000): Promise<void> {
    this.ensureActive()
    if (!receipt.turnId) return
    const attempt = this.#submissionStore.getSnapshot()
    const matchesAttempt = (candidate: SubmissionAttempt | undefined) =>
      candidate?.requestId === receipt.requestId && candidate.dispatch?.turnId === receipt.turnId
    const tracked = matchesAttempt(attempt)
    if (tracked) this.#submissionStore.reconcile(this.lease.getSnapshot())
    const isAccepted = () => {
      if (tracked) {
        const current = this.#submissionStore.getSnapshot()
        return matchesAttempt(current) && current?.phase === 'accepted'
      }
      const state = this.lease.getSnapshot()
      return state?.activeTurn?.id === receipt.turnId || state?.turns?.some(turn => turn.id === receipt.turnId) === true
    }
    if (isAccepted()) return

    await new Promise<void>((resolve, reject) => {
      let settled = false
      let unsubscribe = () => {}
      let unsubscribeSubmission = () => {}
      const finish = (callback: () => void) => {
        if (settled) return
        settled = true
        clearTimeout(timer)
        unsubscribe()
        unsubscribeSubmission()
        this.#disposeListeners.delete(onDisposed)
        callback()
      }
      const rejectUnknown = (error?: Error) => {
        if (tracked && matchesAttempt(this.#submissionStore.getSnapshot())) {
          this.#submissionStore.failed(receipt.requestId)
        }
        reject(tracked ? new SubmissionRecoveryError(this.#recovery, 'unknown', error) : error)
      }
      const onDisposed = (error: Error) => finish(() => rejectUnknown(error))
      const timer = setTimeout(
        () =>
          finish(() =>
            rejectUnknown(
              new Error(
                tracked
                  ? 'The host did not confirm acceptance before the acknowledgement window closed.'
                  : 'The first AHP turn was not accepted before the acknowledgement window closed.',
              ),
            ),
          ),
        timeoutMs,
      )
      this.#disposeListeners.add(onDisposed)
      unsubscribe = this.lease.subscribe(() => {
        if (isAccepted()) finish(resolve)
      })
      if (tracked) {
        const check = () => {
          if (!matchesAttempt(this.#submissionStore.getSnapshot())) {
            finish(() => rejectUnknown(new Error('The submission attempt was superseded.')))
          } else if (isAccepted()) {
            finish(resolve)
          } else if (this.#submissionStore.getSnapshot()?.phase === 'rejected') {
            finish(() => reject(new SubmissionRecoveryError(this.#recovery, 'rejected')))
          }
        }
        unsubscribeSubmission = this.#submissionStore.subscribe(check)
        check()
      }
      if (this.#disposed) onDisposed(new Error('established AHP session client is disposed'))
    })
  }

  private ensureActive(): void {
    if (this.#terminalError) throw this.#terminalError
    if (this.#disposed) throw new Error('established AHP session client is disposed')
  }

  private nextReceipt(
    details: Omit<DispatchReceipt, 'requestId' | 'clientSeq'>,
    requestId: string = crypto.randomUUID(),
  ): DispatchReceipt {
    return {
      requestId,
      clientSeq: ++this.#clientSeq,
      ...details,
    }
  }

  private async ensureMutationLane(requestId?: string): Promise<void> {
    this.ensureActive()
    if (!this.#isResumptionRecoveryActive()) {
      const reconnected = await this.lease.ensureLive(
        requestId ? () => this.directTurnTelemetry?.readinessRequired(requestId) : undefined,
      )
      if (reconnected) await this.lease.whenConnected()
      if (this.lease.getPassive()) throw new Error('relay connection is read-only')
      if (requestId) this.directTurnTelemetry?.readinessObserved(requestId)
      return
    }

    try {
      let deadline: EstablishedAhpResumptionAttempt | undefined
      let announceDeadline: (attempt: EstablishedAhpResumptionAttempt) => void = () => {}
      const deadlineStarted = new Promise<EstablishedAhpResumptionAttempt>(resolve => {
        announceDeadline = resolve
      })
      const reconnecting = this.lease.ensureLive(() => {
        if (requestId) this.directTurnTelemetry?.readinessRequired(requestId)
        deadline = this.#resumptionAttemptForMutation(true)
        if (deadline) announceDeadline(deadline)
      })
      deadline ??= this.#resumptionAttemptForMutation(false)
      let reconnected: boolean
      if (deadline) {
        reconnected = await deadline.waitFor(reconnecting)
      } else {
        const reconnectResult = async () => ({kind: 'reconnected' as const, result: await reconnecting})
        const announcedDeadline = async () => ({kind: 'deadline' as const, attempt: await deadlineStarted})
        const first = await Promise.race([reconnectResult(), announcedDeadline()])
        if (first.kind === 'deadline') {
          deadline = first.attempt
          reconnected = await deadline.waitFor(reconnecting)
        } else {
          reconnected = first.result
        }
      }
      if (reconnected) {
        deadline ??= this.#resumptionAttemptForMutation(false)
        if (deadline) await deadline.waitFor(this.lease.whenConnected(undefined, undefined, deadline.signal))
        else await this.lease.whenConnected()
      }
    } catch (error) {
      if (error instanceof EstablishedAhpSessionResumptionTimeoutError) {
        this.#onTerminalResumptionTimeout(error)
      }
      throw error
    }
    if (this.lease.getPassive()) throw new Error('relay connection is read-only')
    if (requestId) this.directTurnTelemetry?.readinessObserved(requestId)
  }

  private async submit(
    prompt: PreparedPrompt,
    requestId?: string,
    beforeDispatch?: () => Promise<void>,
    directTurnTelemetryContext?: DirectTurnTelemetryContext | null,
  ): Promise<DispatchReceipt> {
    this.ensureActive()
    const blocker = this.#submissionBlocker()
    if (blocker) throw blocker
    const cancellationVersion = this.#cancellationVersion
    const submissionRequestId = requestId ?? crypto.randomUUID()
    this.#submissionStore.begin(submissionRequestId, prompt)
    const preparation = this.#submissionStore.getSnapshot()
    let preparationInvalidated = false
    const invalidatePreparation = () => {
      if (!preparation || this.#submissionStore.getSnapshot() !== preparation) return
      preparationInvalidated = true
      this.#submissionStore.failed(submissionRequestId)
    }
    this.#invalidatePreparation = invalidatePreparation
    const releaseRecovery = observeRelayRecoveryPreparation(this.identity, submissionRequestId)
    const telemetryRequestId = directTurnTelemetryContext === null ? undefined : submissionRequestId
    try {
      if (telemetryRequestId) {
        this.directTurnTelemetry?.beginSubmission({
          requestId: telemetryRequestId,
          turnKind: 'existing_chat',
          startPath: 'active',
          ...(prompt.model?.id ? {modelId: prompt.model.id} : {}),
          ...(directTurnTelemetryContext ? {context: directTurnTelemetryContext} : {}),
        })
      }
      const commandObserver = clientCommandObserver(prompt)
      if (commandObserver) await observeMutationWait(commandObserver, () => this.ensureMutationLane(telemetryRequestId))
      else await this.ensureMutationLane(telemetryRequestId)
      if (beforeDispatch) await beforeDispatch()
      this.#ensureSubmissionCurrent(cancellationVersion)
      return this.#dispatchTurn(prompt, submissionRequestId, telemetryRequestId ?? null)
    } catch (error) {
      if (!preparationInvalidated) this.#submissionStore.failed(submissionRequestId)
      const attempt = !preparationInvalidated ? this.#submissionStore.getSnapshot() : undefined
      if (telemetryRequestId && !attempt?.dispatch) this.directTurnTelemetry?.preDispatchError(telemetryRequestId)
      if (!this.lease.dispatchTurnTracked) throw error
      if (error instanceof DOMException && error.name === 'AbortError' && !attempt?.dispatch) throw error
      throw SubmissionRecoveryError.forAttempt(this.#recovery, attempt, error)
    } finally {
      if (this.#invalidatePreparation === invalidatePreparation) this.#invalidatePreparation = undefined
      releaseRecovery?.()
    }
  }

  #submissionBlocker(cause?: unknown): UserFacingSubmissionError | undefined {
    const attempt = this.#submissionStore.getSnapshot()
    if (this.lease.dispatchTurnTracked && attempt?.phase === 'preparing') {
      return new UserFacingSubmissionError('Another message is still preparing. Wait for it before sending again.')
    }
    if (submissionIsUnsettled(attempt)) return new SubmissionRecoveryError(this.#recovery, 'unknown', cause)
    return undefined
  }

  #ensureSubmissionCurrent(cancellationVersion: number): void {
    this.ensureActive()
    if (cancellationVersion !== this.#cancellationVersion) {
      throw new DOMException('The submission was cancelled.', 'AbortError')
    }
    if (this.lease.getPassive()) throw new Error('relay connection is read-only')
  }

  #dispatchTurn(
    prompt: PreparedPrompt,
    requestId: string,
    telemetryRequestId: string | null = requestId,
  ): DispatchReceipt {
    if (telemetryRequestId) this.directTurnTelemetry?.turnDispatching(telemetryRequestId)
    const turnId = observeSessionCommand(
      clientCommandObserver(prompt),
      this.identity,
      () =>
        this.lease.dispatchTurnTracked
          ? this.lease.dispatchTurnTracked(
              {
                text: prompt.text,
                attachments: attachmentsFor(prompt),
                model: prompt.model,
                agent: prompt.agent,
                contributedInstructions: prompt.contributedInstructions,
                modelText: prompt.modelText,
                space: prompt.displaySpace,
              },
              dispatch => {
                this.#submissionStore.dispatched(requestId, dispatch)
                if (!this.#submissionStore.getSnapshot()?.receipt) {
                  this.#submissionStore.receipt(this.nextReceipt({turnId: dispatch.turnId}, requestId))
                }
              },
            )
          : prompt.displaySpace !== undefined
            ? this.lease.dispatchTurn(
                prompt.text,
                attachmentsFor(prompt),
                prompt.model,
                prompt.agent,
                prompt.contributedInstructions,
                prompt.modelText,
                prompt.displaySpace,
              )
            : prompt.modelText !== undefined
              ? this.lease.dispatchTurn(
                  prompt.text,
                  attachmentsFor(prompt),
                  prompt.model,
                  prompt.agent,
                  prompt.contributedInstructions,
                  prompt.modelText,
                )
              : prompt.contributedInstructions === undefined
                ? this.lease.dispatchTurn(prompt.text, attachmentsFor(prompt), prompt.model, prompt.agent)
                : this.lease.dispatchTurn(
                    prompt.text,
                    attachmentsFor(prompt),
                    prompt.model,
                    prompt.agent,
                    prompt.contributedInstructions,
                  ),
      requestId,
    )
    if (telemetryRequestId) this.directTurnTelemetry?.turnDispatched(telemetryRequestId, turnId)
    const receipt = this.#submissionStore.getSnapshot()?.receipt ?? this.nextReceipt({turnId}, requestId)
    this.#submissionStore.receipt(receipt)
    return receipt
  }

  private async queue(prompt: PreparedPrompt, beforeDispatch?: () => Promise<void>): Promise<DispatchReceipt> {
    const requestId = crypto.randomUUID()
    const releaseRecovery = observeRelayRecoveryPreparation(this.identity, requestId)
    try {
      const cancellationVersion = this.#cancellationVersion
      const commandObserver = clientCommandObserver(prompt)
      if (commandObserver) await observeMutationWait(commandObserver, () => this.ensureMutationLane())
      else await this.ensureMutationLane()
      if (beforeDispatch) await beforeDispatch()
      this.#ensureSubmissionCurrent(cancellationVersion)
      const pendingMessageId = observeSessionCommand(
        commandObserver,
        this.identity,
        () =>
          prompt.contributedInstructions === undefined
            ? this.lease.queueMessage(prompt.text, attachmentsFor(prompt), prompt.model, prompt.agent)
            : this.lease.queueMessage(
                prompt.text,
                attachmentsFor(prompt),
                prompt.model,
                prompt.agent,
                prompt.contributedInstructions,
              ),
        requestId,
      )
      return Promise.resolve(this.nextReceipt({pendingMessageId}, requestId))
    } finally {
      releaseRecovery?.()
    }
  }

  private async steer(
    prompt: PreparedPrompt,
    steeringId?: string,
    beforeDispatch?: () => Promise<void>,
    directTurnRequestId?: string,
  ): Promise<DispatchReceipt> {
    const cancellationVersion = this.#cancellationVersion
    const requestId = directTurnRequestId ?? crypto.randomUUID()
    const releaseRecovery = observeRelayRecoveryPreparation(this.identity, requestId)
    this.directTurnTelemetry?.beginSubmission({
      requestId,
      turnKind: 'existing_chat',
      startPath: 'active',
      ...(prompt.model?.id ? {modelId: prompt.model.id} : {}),
    })
    try {
      const commandObserver = clientCommandObserver(prompt)
      if (commandObserver) await observeMutationWait(commandObserver, () => this.ensureMutationLane(requestId))
      else await this.ensureMutationLane(requestId)
      if (beforeDispatch) await beforeDispatch()
      this.#ensureSubmissionCurrent(cancellationVersion)
      // Ownership acknowledgement can arrive after the turn finishes. Dispatch
      // directly while idle rather than leaving steering waiting for another turn.
      if (!this.view.getSnapshot().state?.activeTurn) {
        const blocker = this.#submissionBlocker()
        if (blocker) throw blocker
        this.#submissionStore.begin(requestId, prompt)
        return this.#dispatchTurn(prompt, requestId)
      }
      this.directTurnTelemetry?.discardSubmission(requestId)
      const pendingMessageId = observeSessionCommand(
        commandObserver,
        this.identity,
        () =>
          prompt.contributedInstructions === undefined
            ? this.lease.steerMessage(prompt.text, attachmentsFor(prompt), prompt.model, steeringId, prompt.agent)
            : this.lease.steerMessage(
                prompt.text,
                attachmentsFor(prompt),
                prompt.model,
                steeringId,
                prompt.agent,
                prompt.contributedInstructions,
              ),
        requestId,
      )
      return this.nextReceipt({pendingMessageId}, requestId)
    } catch (error) {
      const attempt = this.#submissionStore.getSnapshot()
      if (attempt?.requestId === requestId) this.#submissionStore.failed(requestId)
      if (attempt?.requestId === requestId && attempt.dispatch) {
        throw SubmissionRecoveryError.forAttempt(this.#recovery, attempt, error)
      }
      this.directTurnTelemetry?.preDispatchError(requestId)
      if (attempt?.requestId === requestId && this.lease.dispatchTurnTracked) {
        throw new SubmissionRecoveryError(this.#recovery, 'unsent', error)
      }
      throw error
    } finally {
      releaseRecovery?.()
    }
  }

  private async sendNow(message: EstablishedAhpPendingMessage): Promise<DispatchReceipt> {
    const observer = clientCommandObserver(message.prompt)
    if (observer) {
      // Sending now also removes the old queue entry. Both real sends belong to
      // this one user revision; a successful steering send alone is insufficient.
      const revision = observeQueuedCommandRevision(observer, message.id, message.prompt, this.identity)
      const prompt = {...message.prompt}
      bindClientCommand(prompt, revision.message)
      try {
        const receipt = await this.steer(prompt, message.id)
        await this.removeQueued(message.id, revision.removal)
        return receipt
      } catch (error) {
        revision.fail()
        throw error
      }
    }
    const receipt = await this.steer(message.prompt, message.id)
    await this.removeQueued(message.id)
    return receipt
  }

  private async removeQueued(id: string, observer?: ClientCommandObserver): Promise<void> {
    if (observer) await observeMutationWait(observer, () => this.ensureMutationLane())
    else await this.ensureMutationLane()
    observeSessionCommand(observer, this.identity, () => this.lease.removeQueuedMessage(id))
    return Promise.resolve()
  }

  private cancel(turnId?: string): StopOutcome {
    this.ensureActive()
    const activeTurnId = turnId ?? this.lease.getSnapshot()?.activeTurn?.id
    const accepted = observeSessionCommand(clientStopObserver(), this.identity, () =>
      this.lease.cancelTurn(activeTurnId),
    )
    if (accepted) {
      this.#cancellationVersion++
      this.#invalidatePreparation?.()
    }
    if (accepted && activeTurnId) {
      this.#optimisticallyCancelledTurnId = activeTurnId
      this.#viewSnapshot = this.readViewSnapshot()
      for (const listener of this.#viewListeners) listener()
    }

    return {
      accepted,
      ...(activeTurnId ? {turnId: activeTurnId} : {}),
    }
  }

  private claimActiveClient(tools: Parameters<RelayLease['claimActiveClient']>[0]): Promise<void> {
    this.ensureActive()
    return this.lease.claimActiveClient(tools)
  }

  private respondToPrompt(input: InputCompletion): void {
    this.ensureActive()
    this.lease.completeInput({
      requestId: input.requestId,
      response: input.response,
      ...(input.answers ? {answers: input.answers} : {}),
    })
  }

  private confirmTool(confirmation: ToolConfirmationResponse): void {
    this.ensureActive()
    this.lease.confirmToolCall(confirmation)
  }

  private completeTool(input: {turnId: string; toolCallId: string; result: ToolCallResult}): void {
    this.ensureActive()
    this.lease.completeToolCall(input)
  }

  private async setApproveAll(enabled: boolean): Promise<void> {
    await this.setApprovalMode(enabled ? 'allow-all' : 'manual')
  }

  private async setApprovalMode(mode: SessionApprovalMode): Promise<void> {
    this.ensureActive()
    await this.ensureMutationLane()
    await this.lease.setApprovalMode(mode)
  }

  private setMode(mode: string): Promise<void> {
    this.ensureActive()
    return this.lease.setMode(mode)
  }
}
